import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Tabs,
  Tab,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { IEventSession } from "../../providers/eventSessions/eventsSessions.interface";

interface SocketMetricsItemProps {
  session: IEventSession;
  date: Date;
  metricsData: {
    socketPeakData: any;
    socketHistoryData: any;
    socketTypesData: any;
  };
}

const SocketMetricsItem: React.FC<SocketMetricsItemProps> = ({
  session,
  date,
  metricsData
}) => {
  const { t } = useTranslation();
  const [tabIndex, setTabIndex] = useState(0);
  const [timeZoneDisplay, setTimeZoneDisplay] = useState<string>("");
  
  const { socketPeakData, socketHistoryData, socketTypesData } = metricsData;

  // Calcular informações de fuso horário no momento da renderização
  useState(() => {
    const now = new Date();
    const timeZoneString = now
      .toLocaleTimeString("en-us", { timeZoneName: "short" })
      .split(" ")[2];
    const timeZoneOffset = -now.getTimezoneOffset() / 60;
    const offsetSign = timeZoneOffset >= 0 ? "+" : "-";
    const absoluteOffset = Math.abs(timeZoneOffset);

    setTimeZoneDisplay(`${timeZoneString} (UTC${offsetSign}${absoluteOffset})`);
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  // Função para converter UTC para hora local
  const formatTimeToLocal = (timeString: string): string => {
    if (!timeString) return "";

    // Se for formato de hora completo com data (ISO)
    if (timeString.includes("T")) {
      const date = new Date(timeString);
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    // Se for formato HH:MM ou similar
    if (timeString.includes(":")) {
      // Assumindo que a string está no formato "YYYY-MM-DD:HH" ou "YYYY-MM-DD:HH:MM"
      const parts = timeString.split(":");
      const datePart = parts[0]; // YYYY-MM-DD
      const hourPart = parts.length > 1 ? parts[1] : "00"; // HH
      const minutePart = parts.length > 2 ? parts[2] : "00"; // MM ou '00' se não tiver minutos

      // Criar uma data UTC
      const date = new Date(`${datePart}T${hourPart}:${minutePart}:00Z`);

      // Converter para local
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    return timeString;
  };

  // Formatar dados para o gráfico de histórico com horas locais
  const prepareHistoryChartData = useMemo(() => {
    if (
      !socketHistoryData ||
      !socketHistoryData.history ||
      socketHistoryData.history.length === 0
    )
      return [];

    return socketHistoryData.history.map((item) => {
      // Extrair hora e converter para local
      let localTime;

      if (item.minute.includes(":")) {
        localTime = formatTimeToLocal(item.minute);
      } else {
        // Fallback se o formato for diferente
        localTime = item.minute;
      }

      return {
        time: localTime,
        connections: item.connectionCount,
        // Mantenha o valor original para debugging
        originalTime: item.minute,
      };
    });
  }, [socketHistoryData]);

  // Formatar dados para o gráfico de tipos de conexão com horas locais
  const prepareTypesChartData = useMemo(() => {
    if (!socketTypesData) return [];

    // Se temos dados de tipos por hora
    if (socketTypesData.typeStats && socketTypesData.typeStats.length > 0) {
      const allTypes = new Set<string>();

      // Coletar todos os tipos únicos
      socketTypesData.typeStats.forEach((stat) => {
        Object.keys(stat.connectionTypes || {}).forEach((type) => {
          allTypes.add(type);
        });
      });

      // Criar dados formatados para o gráfico
      return socketTypesData.typeStats.map((stat) => {
        // Converter hora para formato local
        const localHour = formatTimeToLocal(stat.hour);

        const result: any = {
          hour: localHour,
          // Manter valor original para debug
          originalHour: stat.hour,
        };

        Array.from(allTypes).forEach((type) => {
          result[type] = stat.connectionTypes?.[type] || 0;
        });

        return result;
      });
    }

    // Se temos apenas dados de tipos para uma hora específica
    if (socketTypesData.connectionTypes) {
      return Object.entries(socketTypesData.connectionTypes).map(
        ([type, count]) => ({
          type,
          count,
        })
      );
    }

    return [];
  }, [socketTypesData]);

  // Função para gerar cores consistentes baseadas em uma string
  const getColorForString = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const color = (hash & 0x00ffffff)
      .toString(16)
      .toUpperCase()
      .padStart(6, "0");
    return `#${color}`;
  };

  // Renderizar o conteúdo com base na tab selecionada
  const renderContent = () => {
    // Se não temos dados ou dados estão vazios
    const hasPeakData = socketPeakData && socketPeakData.peakConnections;
    const hasHistoryData = socketHistoryData && socketHistoryData.history && socketHistoryData.history.length > 0;
    const hasTypesData = socketTypesData && 
                        ((socketTypesData.typeStats && socketTypesData.typeStats.length > 0) || 
                         (socketTypesData.connectionTypes && Object.keys(socketTypesData.connectionTypes).length > 0));
    
    if (!hasPeakData && !hasHistoryData && !hasTypesData) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="200px"
        >
          <Typography>
            No connection data available for this session and date.
          </Typography>
        </Box>
      );
    }

    // Renderizar com base na tab selecionada
    switch (tabIndex) {
      case 0: // Visão geral / pico
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Connection data excludes admin users to provide accurate
                audience metrics. All times are shown in your local timezone:{" "}
                <strong>{timeZoneDisplay}</strong>
              </Typography>
            </Alert>
            <Card elevation={3} sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Peak Connection Stats
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={1}
                      sx={{
                        p: 3,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                      }}
                    >
                      <Typography variant="subtitle1" gutterBottom>
                        Peak Connections
                      </Typography>
                      <Typography variant="h3" color="primary">
                        {socketPeakData?.peakConnections || "N/A"}
                      </Typography>
                      {socketPeakData?.peakTime && (
                        <Typography variant="body2" color="textSecondary">
                          at {formatTimeToLocal(socketPeakData.peakTime)}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper elevation={1} sx={{ p: 3, height: "100%" }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Session Information
                      </Typography>
                      <Typography variant="body1">
                        <strong>Name:</strong> {session.name}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Room ID:</strong> {session.chatRoom}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Date:</strong>{" "}
                        {socketPeakData?.date || date.toISOString().split("T")[0]}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        );

      case 1: // Gráfico de histórico
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Connection data excludes admin users to provide accurate
                audience metrics. All times are shown in your local timezone:{" "}
                <strong>{timeZoneDisplay}</strong>
              </Typography>
            </Alert>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Connection History
                </Typography>
                {prepareHistoryChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={prepareHistoryChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="time"
                        label={{
                          value: "Time (Local)",
                          position: "insideBottomRight",
                          offset: -10,
                        }}
                      />
                      <YAxis
                        label={{
                          value: "Connections",
                          angle: -90,
                          position: "insideLeft",
                        }}
                      />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="connections"
                        stroke="#8884d8"
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography align="center" sx={{ my: 5 }}>
                    No history data available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        );

      case 2: // Tipos de conexão
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Connection data excludes admin users to provide accurate
                audience metrics. All times are shown in your local timezone:{" "}
                <strong>{timeZoneDisplay}</strong>
              </Typography>
            </Alert>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Connection Types
                </Typography>
                {socketTypesData ? (
                  <>
                    {socketTypesData.typeStats &&
                    socketTypesData.typeStats.length > 0 ? (
                      // Gráfico melhorado para tipos de conexão ao longo do tempo
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
                          Connection types distribution over time
                        </Typography>
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart 
                            data={prepareTypesChartData}
                            barCategoryGap="15%"
                            margin={{ top: 5, right: 30, left: 20, bottom: 25 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="hour" 
                              label={{
                                value: "Hour (Local)",
                                position: "insideBottomRight",
                                offset: -10,
                              }}
                              tick={{ fontSize: 12 }}
                            />
                            <YAxis 
                              label={{
                                value: "Number of Connections",
                                angle: -90,
                                position: "insideLeft",
                                offset: -5,
                              }}
                            />
                            <Tooltip
                              formatter={(value, name) => [`${value} connections`, name]}
                              labelFormatter={(label) => `Time: ${label}`}
                              contentStyle={{ backgroundColor: '#f5f5f5', borderRadius: '8px' }}
                            />
                            <Legend 
                              layout="horizontal"
                              verticalAlign="top"
                              wrapperStyle={{ paddingBottom: '10px' }}
                            />
                            {Object.keys(prepareTypesChartData[0] || {})
                              .filter(
                                (key) => key !== "hour" && key !== "originalHour"
                              )
                              .map((type, index) => (
                                <Bar
                                  key={type}
                                  dataKey={type}
                                  fill={getColorForString(type)}
                                  name={type}
                                  stackId="stack"
                                  radius={[4, 4, 0, 0]}
                                />
                              ))}
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : socketTypesData.connectionTypes ? (
                      // Visualização melhorada para um único conjunto de dados de tipos
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
                          Distribution of connection types
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <TableContainer component={Paper} sx={{ mb: 2, boxShadow: 2 }}>
                              <Table>
                                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                                  <TableRow>
                                    <TableCell>Connection Type</TableCell>
                                    <TableCell align="right">Count</TableCell>
                                    <TableCell align="right">Percentage</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(() => {
                                    const entries = Object.entries(socketTypesData.connectionTypes);
                                    const total = entries.reduce((sum, [_, count]) => sum + (count as number), 0);
                                    
                                    return entries.map(([type, count]) => (
                                      <TableRow key={type} hover>
                                        <TableCell component="th" scope="row">
                                          <Chip 
                                            label={type} 
                                            variant="outlined" 
                                            sx={{ 
                                              backgroundColor: `${getColorForString(type)}22`,
                                              borderColor: getColorForString(type),
                                              fontWeight: 500 
                                            }} 
                                          />
                                        </TableCell>
                                        <TableCell align="right">{count}</TableCell>
                                        <TableCell align="right">
                                          {total > 0 ? `${Math.round((count as number / total) * 100)}%` : '0%'}
                                        </TableCell>
                                      </TableRow>
                                    ));
                                  })()}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2, height: '100%', boxShadow: 2 }}>
                              <ResponsiveContainer width="100%" height={220}>
                                <PieChart>
                                  <Pie
                                    data={Object.entries(socketTypesData.connectionTypes).map(([type, count]) => ({
                                      name: type,
                                      value: count as number
                                    }))}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="value"
                                    label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    labelLine={false}
                                  >
                                    {Object.keys(socketTypesData.connectionTypes).map((type, index) => (
                                      <Cell key={`cell-${index}`} fill={getColorForString(type)} />
                                    ))}
                                  </Pie>
                                  <Tooltip formatter={(value) => [`${value} connections`, 'Count']} />
                                </PieChart>
                              </ResponsiveContainer>
                            </Paper>
                          </Grid>
                        </Grid>
                      </Box>
                    ) : (
                      <Typography align="center" sx={{ my: 5 }}>
                        No connection type data available
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography align="center" sx={{ my: 5 }}>
                    No connection type data available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <Box sx={{ mb: 3 }}>
        <Tabs value={tabIndex} onChange={handleTabChange} centered>
          <Tab label="Overview" />
          <Tab label="Connection History" />
          <Tab label="Connection Types" />
        </Tabs>
      </Box>
      {renderContent()}
    </div>
  );
};

export default SocketMetricsItem;